import { useState } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface SensorTagsDescriptionProps {
  tags: string[];
  description: string;
  onChange: (field: string, value: string[] | string) => void;
}

export function SensorTagsDescription({
  tags,
  description,
  onChange,
}: SensorTagsDescriptionProps) {
  const [open, setOpen] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const addTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange('tags', [...tags, trimmed]);
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    onChange('tags', tags.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground w-full py-1">
        <ChevronRight
          className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`}
        />
        Tags & Description
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pt-2 pl-5">
        <div>
          <Label>Tags</Label>
          <div className="flex flex-wrap gap-1 mb-2">
            {tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="gap-1">
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type and press Enter to add..."
          />
        </div>
        <div>
          <Label htmlFor="sensor-description">Description</Label>
          <Textarea
            id="sensor-description"
            value={description}
            onChange={(e) => onChange('description', e.target.value)}
            rows={3}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
